import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Beforeunload } from 'react-beforeunload';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { ThemeProvider } from '@material-ui/core';
import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import { baseApiReducer } from './global/ducks/base-api';
import { globalizationReducer } from './global/ducks/globalization';
import { tcsmEpiApiReducerWithDefault } from './global/ducks/epi-api';
import { personalInfoReducerByApp } from './pages/account-info/features/personal-info/personal-info-slice';
import { employmentReducer } from './pages/arc/features/employment/employment-slice';
import { institutionReducer } from './pages/arc/features/institutions/institution-slice';
import { compensationHistoryReducer } from './pages/arc/features/compensation/compensation-history/compensation-history-slice';
import { paginationReducer } from './pages/arc/shared-slices/pagination-slice';
import { apiMiddleware } from './global/middleware/api-middleware';
import tcsmTheme from './global/constants/theme/tcsm-theme';
import { DefaultTcsmEpiApiState, TcsmStates } from './root/tcsm-model';
import { relationshipsReducerByApp } from './pages/account-info/features/relationships/relationships-slice';
import { dependentsReducerByApp } from './pages/account-info/features/dependents/dependents-slice';
import { documentUploadReducerByApp } from './pages/account-info/features/personal-info/document-upload-slice';
import { TcsmApp } from './root/tcsm-app';
import { CustomAuth } from './global/components/custom-auth';
import { TopNavigation } from './global/components/navigation/top-navigation';
import { appContextApiReducer } from './global/ducks/app-context';
import { authenticatedReducer } from './global/ducks/authenticated-slice';
import { personSearchReducer } from './pages/arc/features/person-search/person-search-slice';
import { lovProcReducer } from './global/ducks/lov-proc-api';
import { billingAccountReducer } from './pages/arc/features/billing-accounts/billing-accounts-slice';
import { participantsReducer } from './pages/arc/features/participants/participants-slices';
import { officerAdminReducer } from './pages/arc/features/institution-officers/institution-officers-slices';
import { groupsReducer } from './pages/arc/features/groups/groups-slice';
import { annualEnrollmentReducerByApp } from './pages/annual-enrollment/annual-enrollment-slice';
import { midYearEnrollmentReducer } from './pages/arc/features/mid-year/mid-year-slices';
import { Footer } from './global/components/footer';
import { pensionEnrollmentReducer } from './pages/arc/features/pension-enrollment/pension-enrollment-slice';
import { newHireReducer } from './pages/arc/features/new-hire/new-hire-slice';
import { documentDownloadReducer } from './pages/arc/features/document-download/document-download-slice';
import { notificationReducer } from './pages/notification/notification-slice';
import { myCpgPodReducer } from './pages/mycpg-pods/mycpg-pod-slice';
import { employmentCompensationReducer } from './pages/arc/features/employment-compensation/employment-compensation-slices';
import { reportAccessReducer } from './pages/arc/features/report/report-slices';
import { TcsmEnvironment } from './root/tcsm-environment';
import { MapLanding } from './pages/arc/features/map-landing/map-landing';
import { DemographicAppKeys, DemographicsContext } from './global/models/demographics-model';
import { MyCpgLandingPage } from './pages/mycpg-pods/mycpg-landing-page';
import { HandleOnBeforeUnload } from './global/helpers/analytics-helper';
import { backendSearchReducer } from './backend-search/slices/backend-search-slice';
import { myDocumentsReducer } from './pages/my-documents/my-documents-slice';
import { clergyCompReportReducer } from './pages/clergy-compensation-report/clergy-comp-report-slice';
import { clergyBioReducer } from './pages/clergy-bio/clergy-bio-slice';
import { newHireEnrollmentReducer } from './pages/arc/features/new-hire-enrollment/slice/new-hire-enrollment-slices';
import { participantEnrollmentReducer } from './pages/arc/features/new-participant-enrollment/slice/participant-enrollment-slices';
import { beneficiaciesReducer } from './pages/arc/features/beneficiary/beneficiary-slices';
import { myCpgMidYearEnrollmentReducer } from './pages/account-info/features/mycpg-midyear/my-cpg-mid-year-slice';

export const stateReducer = combineReducers<TcsmStates>({
    apiState: baseApiReducer,
    authenticatedState: authenticatedReducer,
    globalization: globalizationReducer,
    epiProps: tcsmEpiApiReducerWithDefault(DefaultTcsmEpiApiState),
    lovProcState: lovProcReducer,

    employmentState: employmentReducer,
    institutionState: institutionReducer,
    compensationHistoryState: compensationHistoryReducer,
    paginationState: paginationReducer,
    billingAccountsState: billingAccountReducer,
    participantState: participantsReducer,
    personSearchState: personSearchReducer,
    groupsState: groupsReducer,

	personalInfo: personalInfoReducerByApp,
	relationships: relationshipsReducerByApp,
	dependents: dependentsReducerByApp,
	documentUpload: documentUploadReducerByApp,
	officerAdminState: officerAdminReducer,
	appContext: appContextApiReducer,
	annualEnrollment: annualEnrollmentReducerByApp,
	midYearEnrollment: midYearEnrollmentReducer,
	pensionEnrollmentState: pensionEnrollmentReducer,
	newHireState: newHireReducer,
	documentDownload: documentDownloadReducer,
	notificationState: notificationReducer,
	mycpgPodsState: myCpgPodReducer,
	employmentCompensationState: employmentCompensationReducer,
	reportAccessState: reportAccessReducer,
	backendSearchStates: backendSearchReducer,
	myDocumentsState: myDocumentsReducer,
	clergyCompReportState: clergyCompReportReducer,
	clergyBioState: clergyBioReducer,
	newHireEnrollmentState: newHireEnrollmentReducer,
	participantEnrollmentState: participantEnrollmentReducer,
	beneficiariesState: beneficiaciesReducer,
	myCpgMidYearState: myCpgMidYearEnrollmentReducer,
});

const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const stateStore = createStore(stateReducer, composeEnhancers(
    applyMiddleware(
        apiMiddleware,
    ),
));

const mount = document.getElementById('tcsm-root');
if (mount) {
    const appSettings = (window as any).bootstrap;

	ReactDOM.render(
		<Provider store={stateStore}>
			<ThemeProvider theme={tcsmTheme}>
				<Beforeunload onBeforeunload={HandleOnBeforeUnload} />
				<BrowserRouter>
					<Switch>
						<Route exact path="/services/_environmentDebug">
							<TcsmEnvironment
								contentId={appSettings?.contentId}
								language={appSettings?.currentCulture}
								availableLanguages={appSettings?.availableCultures} />
						</Route>
						<Route exact path="/services/debugdashboard/mycpg">
							<DemographicsContext.Provider value={DemographicAppKeys.myCPG}>
								<MyCpgLandingPage dashboardMode podsSettingsId={appSettings?.podsSettingsId} appConfig={appSettings} />
							</DemographicsContext.Provider>
						</Route>
						<Route exact path="/services/debugdashboard/map">
							<MapLanding dashboardMode mapPortalSettingsId={appSettings?.mapPortalSettingsId} />
						</Route>
						<Route>
							<CustomAuth redirectUri={`${window.location.origin}/services`}>
								<TopNavigation navigation={{
									...appSettings?.navigation,
									...appSettings?.cicSettings,
								}} />
								<div className='tcsm-wrapper-inner'>
									<TcsmApp
										appConfig={appSettings}
									/>
								</div>
								<Footer footer={appSettings?.footer} />
							</CustomAuth>
						</Route>
					</Switch>
				</BrowserRouter>
			</ThemeProvider>
		</Provider>, mount,
	);
}